import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import {
  CLUB_BRAND_ADVANCED_UI,
  DEFAULT_CLUB_TEXT_COLOR,
  PRESET_CLUB_BRAND_COLORS,
} from "../../config/clubWebsitePages";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import { resolveCloudinaryImageSrc } from "../../helpers/helper.service";
import "./clubBrandingFields.css";

const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/** Stored as string 0–1 (e.g. "0.65"); empty = full opacity on consumer. */
function parseBgOpacity01(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return 1;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return 1;
  return Math.min(1, Math.max(0, n));
}

/**
 * @param {{
 *   t: (k: string) => string,
 *   values: Record<string, string>,
 *   onFieldChange: (key: string, value: string) => void,
 *   primaryFallback?: string,
 *   onBackgroundImageUpload?: (fieldKey: string, file: File) => Promise<void>,
 *   uploadsDisabled?: boolean,
 * }} props
 */
const ClubBrandingAdvancedFields = ({
  t,
  values,
  onFieldChange,
  primaryFallback = "#000000",
  onBackgroundImageUpload,
  uploadsDisabled = false,
}) => {
  const [uploadingKey, setUploadingKey] = useState(null);
  const cloudinaryBase = getCloudinaryMediaBaseUrl();

  const colorValueForPicker = (raw, fallbackHex) => {
    const s = String(raw || "").trim();
    if (s && hexPattern.test(s)) return s;
    const fb = String(fallbackHex || primaryFallback || "").trim();
    return hexPattern.test(fb) ? fb : "#000000";
  };

  const backgroundPreviewSrc = (raw) => {
    const url = String(raw || "").trim();
    if (!url) return "";
    if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
    return resolveCloudinaryImageSrc(url, cloudinaryBase, "");
  };

  const onBgFileChange = async (fieldKey, ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file || !onBackgroundImageUpload) return;
    setUploadingKey(fieldKey);
    try {
      await onBackgroundImageUpload(fieldKey, file);
    } finally {
      setUploadingKey(null);
    }
  };

  return (
    <div className="row mt-2">
      {CLUB_BRAND_ADVANCED_UI.map(
        ({
          key,
          labelKey,
          color,
          wide,
          placeholderKey,
          imageUrl,
          opacityKey,
        }) => (
          <div
            key={key}
            className={wide ? "col-12 mb-3" : "col-md-6 mb-3"}
          >
            <p className="form-label mb-1">{t(labelKey)}</p>
            {imageUrl ? (
              <>
                <div className="club-brand-media-row mb-2">
                  <div
                    className={
                      wide
                        ? "club-brand-thumb-wrap club-brand-thumb-wrap--wide"
                        : "club-brand-thumb-wrap"
                    }
                  >
                    {backgroundPreviewSrc(values[key]) ? (
                      <img
                        src={backgroundPreviewSrc(values[key])}
                        alt=""
                        className="club-brand-thumb"
                        onError={(ev) => {
                          ev.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <span className="club-brand-thumb-placeholder">
                        {t("CLUB_BRAND_IMG_PREVIEW_EMPTY")}
                      </span>
                    )}
                  </div>
                  <div className="club-brand-media-actions">
                    {onBackgroundImageUpload ? (
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                        className="form-control club-brand-file-input"
                        disabled={
                          uploadsDisabled || uploadingKey === key
                        }
                        onChange={(e) => void onBgFileChange(key, e)}
                      />
                    ) : null}
                    {uploadingKey === key ? (
                      <span className="text-muted small">
                        {t("CLUB_BRAND_BG_UPLOADING")}
                      </span>
                    ) : null}
                    {(values[key] || "").trim() ? (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm align-self-start"
                        disabled={uploadsDisabled}
                        onClick={() => {
                          onFieldChange(key, "");
                          if (opacityKey) onFieldChange(opacityKey, "");
                        }}
                      >
                        {t("CLUB_BRAND_BG_CLEAR")}
                      </button>
                    ) : null}
                  </div>
                </div>
                {opacityKey ? (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center text-muted mb-1">
                      <span>{t("CLUB_BRAND_BG_OPACITY")}</span>
                      <span>
                        {Math.round(parseBgOpacity01(values[opacityKey]) * 100)}
                        %
                      </span>
                    </div>
                    <Form.Range
                      min={0}
                      max={100}
                      step={1}
                      disabled={uploadsDisabled}
                      value={Math.round(
                        parseBgOpacity01(values[opacityKey]) * 100
                      )}
                      onChange={(e) => {
                        const pct = Number(e.target.value);
                        const o = Math.min(1, Math.max(0, pct / 100));
                        onFieldChange(
                          opacityKey,
                          String(Number(o.toFixed(3)))
                        );
                      }}
                    />
                    <p className="text-muted mt-1 mb-0">
                      {t("CLUB_BRAND_BG_OPACITY_HELP")}
                    </p>
                  </div>
                ) : null}
              </>
            ) : color ? (
              <>
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {PRESET_CLUB_BRAND_COLORS.map((hex) => (
                    <button
                      key={hex}
                      type="button"
                      className="border rounded"
                      title={hex}
                      aria-label={hex}
                      style={{
                        width: 26,
                        height: 26,
                        background: hex,
                        cursor: "pointer",
                      }}
                      onClick={() => onFieldChange(key, hex)}
                    />
                  ))}
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <input
                    type="color"
                    className="form-control form-control-color"
                    title={t(labelKey)}
                    value={colorValueForPicker(
                      values[key],
                      key === "textcolor"
                        ? DEFAULT_CLUB_TEXT_COLOR
                        : primaryFallback
                    )}
                    onChange={(e) => onFieldChange(key, e.target.value)}
                  />
                  <Form.Control
                    value={values[key] || ""}
                    onChange={(e) => onFieldChange(key, e.target.value)}
                    placeholder="#RRGGBB"
                  />
                </div>
              </>
            ) : (
              <>
                <Form.Control
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={values[key] || ""}
                  onChange={(e) => onFieldChange(key, e.target.value)}
                  placeholder={
                    placeholderKey ? t(placeholderKey) : undefined
                  }
                />
              </>
            )}
          </div>
        )
      )}
      <div className="col-12 mb-3">
        <p className="form-label mb-1">{t("CLUB_BRAND_CUSTOM_CSS")}</p>
        <Form.Control
          as="textarea"
          rows={6}
          value={values.customcss || ""}
          onChange={(e) => onFieldChange("customcss", e.target.value)}
          className="font-monospace"
        />
      </div>
    </div>
  );
};

export default ClubBrandingAdvancedFields;
